import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Vim-style two-key navigation shortcuts: g then target key.
 *
 * - g d → /dashboard
 * - g b → /bots
 * - g p → /portfolio
 * - g r → /risk
 * - g s → /settings
 * - g o → /orders
 * - g w → /wallets
 * - g y → /safety
 * - g i → /signals
 *
 * Also registers global shortcuts:
 * - Cmd/Ctrl+Shift+H → /safety (emergency halt shortcut)
 *
 * All shortcuts are disabled when focus is in an input/textarea/contenteditable.
 */
export function useKeyboardNav() {
  const navigate = useNavigate();
  const pendingG = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function isEditable(target: EventTarget | null): boolean {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      return target.isContentEditable;
    }

    function handleKeydown(e: KeyboardEvent) {
      if (isEditable(e.target)) return;

      // Cmd/Ctrl+Shift+H or Cmd/Ctrl+Shift+K → emergency halt (safety page)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'H' || e.key === 'K')) {
        e.preventDefault();
        navigate('/risk');
        return;
      }

      // Two-key "g then X" navigation
      if (pendingG.current) {
        pendingG.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);

        const routes: Record<string, string> = {
          d: '/dashboard',
          b: '/bots',
          p: '/portfolio',
          r: '/risk',
          s: '/settings',
          o: '/orders',
          w: '/wallets',
          y: '/risk',
          i: '/signals',
          m: '/market-data',
          a: '/admin',
        };

        const target = routes[e.key];
        if (target) {
          e.preventDefault();
          navigate(target);
        }
        return;
      }

      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        pendingG.current = true;
        // Reset after 1s if no second key
        timerRef.current = setTimeout(() => {
          pendingG.current = false;
        }, 1000);
      }
    }

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [navigate]);
}
